import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';
import type { Task } from '../types';

interface TaskFilters { date?: string; category?: string; }

// ── Combined data + mutation hook ────────────────────────────────────────────

export function useTasks(filters: TaskFilters = {}) {
  const { toast }                   = useToast();
  const [tasks,   setTasks]         = useState<Task[]>([]);
  const [loading, setLoading]       = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res    = await api.get<Task[]>('/tasks', { params });
      setTasks(res.data);
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => { refetch(); }, [refetch]);

  const createTask = async (data: Partial<Task>): Promise<Task> => {
    try {
      const res = await api.post<Task>('/tasks', data);
      toast('Task logged', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to log task', 'error');
      throw e;
    }
  };

  const deleteTask = async (id: string): Promise<void> => {
    try {
      await api.delete(`/tasks/${id}`);
      setTasks(prev => prev.filter(t => t.id !== id));
      toast('Task deleted', 'success');
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to delete task', 'error');
      throw e;
    }
  };

  const completeTask = async (id: string): Promise<void> => {
    try {
      await api.put(`/tasks/${id}/complete`);
      toast('Task marked as complete — requester notified', 'success');
      refetch();
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to complete task', 'error');
      throw e;
    }
  };

  return { tasks, loading, refetch, createTask, deleteTask, completeTask };
}

// ── Team tasks hook (IT manager) ──────────────────────────────────────────────

export function useTeamTasks(filters: TaskFilters = {}) {
  const { toast }             = useToast();
  const [tasks,   setTasks]   = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res    = await api.get<Task[]>('/tasks/team', { params });
      setTasks(res.data);
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to load team tasks', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => { refetch(); }, [refetch]);

  return { tasks, loading, refetch };
}

// ── Standalone mutation ───────────────────────────────────────────────────────

export function useCreateTask() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const create = async (data: Partial<Task>): Promise<Task> => {
    setLoading(true);
    try {
      const res = await api.post<Task>('/tasks', data);
      toast('Task logged', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to log task', 'error');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { create, loading };
}
